import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineCurrencyPound, HiOutlineCalendarDays, HiOutlineClock,
  HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineSparkles,
  HiOutlineArrowTrendingUp, HiOutlineUserCircle, HiOutlineCheckCircle
} from 'react-icons/hi2';
import { useAuth } from '../context/AuthContext';
import { useRestaurant } from '../context/RestaurantContext';
import api from '../utils/api';
import TopBar from '../components/TopBar';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FULL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// LOCAL date formatter — never shifts to UTC (avoids timezone bugs)
const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const fmtMoney = (n, c = '£') => `${c}${(n || 0).toFixed(2)}`;
const fmtHrs = (h) => {
  if (!h) return '0h';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm > 0 ? `${hh}h ${mm}m` : `${hh}h`;
};

const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, '0')} ${period}`;
};

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Mon as start
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatRange = (start) => {
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts = { day: 'numeric', month: 'short' };
  return `${start.toLocaleDateString('en-GB', opts)} – ${end.toLocaleDateString('en-GB', opts)}`;
};

const isToday = (date) => {
  const d = new Date();
  return d.toDateString() === date.toDateString();
};

const EmployeeDashboard = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const { activeRestaurant } = useRestaurant();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [data, setData] = useState({ shifts: [], totalHours: 0, totalPay: 0, ratePerHour: 0, employeeName: '' });
  const [monthData, setMonthData] = useState({ totalHours: 0, totalPay: 0 });
  const [loading, setLoading] = useState(true);

  const currency = activeRestaurant?.currency || '£';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Use LOCAL date formatter to avoid timezone shifting the start of the week
      const startDate = fmtDate(weekStart);
      const endDate = new Date(weekStart);
      endDate.setDate(endDate.getDate() + 6);
      const endStr = fmtDate(endDate);

      const r = await api.get(`/shifts/my-shifts?startDate=${startDate}&endDate=${endStr}`);
      setData(r.data);

      // Month — first to last day of current month, in local time
      const now = new Date();
      const mStart = fmtDate(new Date(now.getFullYear(), now.getMonth(), 1));
      const mEnd = fmtDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
      const m = await api.get(`/shifts/my-shifts?startDate=${mStart}&endDate=${mEnd}`);
      setMonthData({ totalHours: m.data.totalHours, totalPay: m.data.totalPay });
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const navigateWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  // Build 7-day grid keyed by LOCAL date string (matches what the server stores)
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    const dateStr = fmtDate(d);
    const shift = data.shifts.find(s => s.date === dateStr);
    days.push({ date: d, dateStr, shift, today: isToday(d) });
  }

  const firstName = (user?.name || data.employeeName || '').split(' ')[0];
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <TopBar title="My Schedule" />

      {/* Hero Header */}
      <div className="emp-hero">
        <div className="emp-hero-grid" />
        <div className="emp-hero-content">
          <div className="emp-hero-text">
            <div className="emp-hero-greeting">{greeting},</div>
            <h1 className="emp-hero-name">{firstName} 👋</h1>
            <p className="emp-hero-sub">
              Here's your schedule and earnings overview
              {activeRestaurant && <> at <strong style={{ color: 'var(--lime-400)' }}>{activeRestaurant.name}</strong></>}
            </p>
          </div>
          <div className="emp-hero-avatar">
            {(firstName[0] || 'U').toUpperCase()}
          </div>
        </div>
      </div>

      <div className="page" style={{ marginTop: -40, position: 'relative', zIndex: 2 }}>
        <div className="page-container" style={{ maxWidth: 1100 }}>

          <div className="emp-stats-grid">
            <StatCard
              variant="lime"
              icon={<HiOutlineCurrencyPound size={26} />}
              label="This Week"
              value={fmtMoney(data.totalPay, currency)}
              meta={`${fmtHrs(data.totalHours)} worked`}
              loading={loading}
            />
            <StatCard
              variant="blue"
              icon={<HiOutlineCalendarDays size={26} />}
              label="This Month"
              value={fmtMoney(monthData.totalPay, currency)}
              meta={`${fmtHrs(monthData.totalHours)} worked`}
              loading={loading}
            />
            <StatCard
              variant="dark"
              icon={<HiOutlineClock size={26} />}
              label="Hourly Rate"
              value={fmtMoney(data.ratePerHour, currency)}
              meta="per hour"
              loading={loading}
            />
          </div>

          <div className="emp-schedule">
            <div className="emp-schedule-head">
              <div>
                <div className="emp-schedule-title-pill">
                  <HiOutlineCalendarDays size={14} />
                  WEEKLY SCHEDULE
                </div>
                <div className="emp-schedule-range">{formatRange(weekStart)}</div>
              </div>
              <div className="emp-schedule-nav">
                <button onClick={() => navigateWeek(-1)} className="emp-nav-btn" aria-label="Previous week">
                  <HiOutlineChevronLeft size={18} />
                </button>
                <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="emp-today-btn">
                  Today
                </button>
                <button onClick={() => navigateWeek(1)} className="emp-nav-btn" aria-label="Next week">
                  <HiOutlineChevronRight size={18} />
                </button>
              </div>
            </div>

            <div className="emp-week-grid">
              {days.map((d, i) => (
                <DayCard key={i} day={d} dayName={DAYS[i]} fullName={FULL_DAYS[i]} currency={currency} />
              ))}
            </div>

            <div className="emp-week-footer">
              <div className="emp-week-foot-stat">
                <HiOutlineCheckCircle size={16} color="var(--lime-500)" />
                <span>{data.shifts.length} shift{data.shifts.length !== 1 ? 's' : ''} this week</span>
              </div>
              <div className="emp-week-foot-stat">
                <HiOutlineArrowTrendingUp size={16} color="var(--blue-500)" />
                <span>Total: <strong>{fmtMoney(data.totalPay, currency)}</strong> ({fmtHrs(data.totalHours)})</span>
              </div>
            </div>
          </div>

          <div className="emp-footnote">
            <HiOutlineSparkles size={16} />
            <span>Got a question about your shifts? Speak to your manager.</span>
          </div>

        </div>
      </div>

      <style>{`
        .emp-hero {
          position: relative;
          background: linear-gradient(135deg, var(--ink-900) 0%, var(--ink-800) 100%);
          padding: 36px 24px 76px;
          overflow: hidden;
        }
        .emp-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 60% 50% at 95% -20%, rgba(155, 242, 19, 0.14) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 0% 120%, rgba(28, 92, 255, 0.18) 0%, transparent 55%);
          pointer-events: none;
        }
        .emp-hero-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0);
          background-size: 24px 24px;
          pointer-events: none;
        }
        .emp-hero-content {
          position: relative;
          z-index: 2;
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .emp-hero-greeting {
          color: rgba(255,255,255,0.55);
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.02em;
          margin-bottom: 2px;
        }
        .emp-hero-name {
          color: #fff;
          font-size: 32px;
          font-weight: 800;
          letter-spacing: -0.025em;
          margin: 0 0 6px;
          line-height: 1.1;
        }
        .emp-hero-sub {
          color: rgba(255,255,255,0.65);
          font-size: 14.5px;
          margin: 0;
          line-height: 1.5;
        }
        .emp-hero-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: var(--lime-400);
          color: var(--ink-900);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          font-weight: 800;
          letter-spacing: -0.02em;
          flex-shrink: 0;
          box-shadow: 0 0 0 4px rgba(155,242,19,0.18), 0 6px 24px rgba(155,242,19,0.4);
        }

        .emp-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
          margin-bottom: 20px;
        }

        .stat-card {
          position: relative;
          padding: 22px;
          border-radius: 18px;
          overflow: hidden;
          color: #fff;
          box-shadow: var(--shadow-md);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: var(--shadow-lg);
        }
        .stat-card-blue {
          background: linear-gradient(135deg, var(--blue-500) 0%, var(--blue-700) 100%);
        }
        .stat-card-lime {
          background: linear-gradient(135deg, var(--lime-400) 0%, var(--lime-500) 100%);
          color: var(--ink-900);
        }
        .stat-card-dark {
          background: linear-gradient(135deg, var(--ink-800) 0%, var(--ink-900) 100%);
        }
        .stat-card::before {
          content: '';
          position: absolute;
          top: -40px;
          right: -40px;
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background: rgba(255,255,255,0.07);
          pointer-events: none;
        }
        .stat-card-lime::before { background: rgba(11,13,18,0.06); }
        .stat-card-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: rgba(255,255,255,0.16);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
          backdrop-filter: blur(4px);
        }
        .stat-card-lime .stat-card-icon {
          background: rgba(11,13,18,0.12);
        }
        .stat-card-label {
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          opacity: 0.85;
          margin-bottom: 4px;
        }
        .stat-card-value {
          font-size: 30px;
          font-weight: 800;
          letter-spacing: -0.025em;
          line-height: 1.1;
          margin-bottom: 6px;
        }
        .stat-card-meta {
          font-size: 13px;
          font-weight: 500;
          opacity: 0.85;
        }

        .emp-schedule {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 24px;
          box-shadow: var(--shadow-sm);
          margin-bottom: 22px;
        }
        .emp-schedule-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .emp-schedule-title-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: var(--blue-50);
          color: var(--blue-700);
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          margin-bottom: 6px;
        }
        .emp-schedule-range {
          font-size: 22px;
          font-weight: 800;
          color: var(--text);
          letter-spacing: -0.02em;
        }
        .emp-schedule-nav {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .emp-nav-btn {
          width: 38px;
          height: 38px;
          border: 1.5px solid var(--border);
          background: var(--surface);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text);
          cursor: pointer;
          transition: all 0.15s;
        }
        .emp-nav-btn:hover {
          background: var(--ink-900);
          color: #fff;
          border-color: var(--ink-900);
        }
        .emp-today-btn {
          padding: 0 14px;
          height: 38px;
          border: 1.5px solid var(--blue-500);
          background: var(--blue-500);
          color: #fff;
          border-radius: 10px;
          cursor: pointer;
          font-family: inherit;
          font-size: 13px;
          font-weight: 700;
          transition: all 0.15s;
        }
        .emp-today-btn:hover {
          background: var(--blue-700);
          border-color: var(--blue-700);
          box-shadow: 0 4px 12px var(--blue-glow);
        }

        .emp-week-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 10px;
        }

        .day-card {
          position: relative;
          padding: 14px 12px;
          border-radius: 14px;
          border: 1.5px solid var(--border);
          background: var(--surface);
          min-height: 200px;
          display: flex;
          flex-direction: column;
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .day-card:hover { box-shadow: var(--shadow-sm); }

        .day-card-today {
          border-color: var(--blue-500);
          background: linear-gradient(180deg, var(--blue-50) 0%, var(--surface) 60%);
          box-shadow: var(--ring-blue);
        }
        .day-card-worked {
          border-color: var(--lime-300);
          background: linear-gradient(180deg, var(--lime-50) 0%, var(--surface) 60%);
        }
        .day-card-today.day-card-worked {
          border-color: var(--blue-500);
          background: linear-gradient(180deg, var(--blue-50) 0%, var(--lime-50) 100%);
        }

        .day-name {
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.1em;
          color: var(--text-muted);
          margin-bottom: 2px;
        }
        .day-num {
          font-size: 24px;
          font-weight: 800;
          color: var(--text);
          letter-spacing: -0.02em;
          line-height: 1;
          margin-bottom: 12px;
        }
        .day-card-today .day-num { color: var(--blue-700); }

        .day-today-tag {
          position: absolute;
          top: 10px;
          right: 10px;
          background: var(--blue-500);
          color: #fff;
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 0.04em;
          padding: 2px 7px;
          border-radius: 6px;
        }

        .shift-block {
          margin-top: auto;
          padding: 10px;
          background: var(--surface);
          border: 1px solid var(--lime-200);
          border-radius: 10px;
          box-shadow: var(--shadow-xs);
        }
        .day-card-today .shift-block {
          border-color: var(--blue-200);
        }
        .shift-time {
          font-size: 12px;
          font-weight: 700;
          color: var(--text);
          margin-bottom: 2px;
          line-height: 1.3;
        }
        .shift-arrow {
          font-size: 11px;
          color: var(--text-muted);
          margin: 1px 0;
        }
        .shift-divider {
          height: 1px;
          background: var(--border);
          margin: 8px 0;
        }
        .shift-stats {
          display: flex;
          align-items: baseline;
          gap: 6px;
        }
        .shift-hours {
          font-size: 11px;
          font-weight: 600;
          color: var(--text-muted);
        }
        .shift-pay {
          font-size: 13.5px;
          font-weight: 800;
          color: var(--lime-700);
          letter-spacing: -0.01em;
        }

        .day-off {
          margin-top: auto;
          text-align: center;
          padding: 12px 8px;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-light);
          letter-spacing: 0.02em;
        }
        .day-off-icon { opacity: 0.5; font-size: 18px; margin-bottom: 4px; display: block; }

        .emp-week-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          margin-top: 18px;
          padding-top: 18px;
          border-top: 1px solid var(--border);
        }
        .emp-week-foot-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13.5px;
          color: var(--text-muted);
          font-weight: 500;
        }
        .emp-week-foot-stat strong { color: var(--text); font-weight: 700; }

        .emp-footnote {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 18px;
          color: var(--text-muted);
          font-size: 13.5px;
          font-weight: 500;
        }

        @media (max-width: 900px) {
          .emp-stats-grid { grid-template-columns: 1fr; gap: 12px; }
          .emp-week-grid {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .day-card {
            min-height: auto;
            flex-direction: row;
            align-items: stretch;
            padding: 14px;
            gap: 14px;
          }
          .day-card > div:first-child {
            min-width: 60px;
          }
          .day-num { margin-bottom: 0; font-size: 22px; }
          .shift-block, .day-off { margin-top: 0; flex: 1; }
          .day-off { text-align: left; padding: 8px 0; }
          .day-off-icon { display: inline-block; margin-right: 6px; vertical-align: middle; }
          .day-today-tag { top: 50%; right: 14px; transform: translateY(-50%); }
        }
        @media (max-width: 600px) {
          .emp-hero { padding: 28px 16px 70px; }
          .emp-hero-name { font-size: 26px; }
          .emp-hero-avatar { width: 52px; height: 52px; font-size: 22px; }
          .emp-schedule { padding: 18px 14px; border-radius: 16px; }
          .emp-schedule-range { font-size: 18px; }
          .stat-card-value { font-size: 26px; }
        }
      `}</style>
    </div>
  );
};

const StatCard = ({ variant, icon, label, value, meta, loading }) => (
  <div className={`stat-card stat-card-${variant}`}>
    <div className="stat-card-icon">{icon}</div>
    <div className="stat-card-label">{label}</div>
    <div className="stat-card-value">{loading ? '—' : value}</div>
    <div className="stat-card-meta">{meta}</div>
  </div>
);

const DayCard = ({ day, dayName, fullName, currency }) => {
  const { date, shift, today } = day;
  const hasShift = shift && (shift.clockIn || shift.clockOut);

  return (
    <div className={`day-card ${today ? 'day-card-today' : ''} ${hasShift ? 'day-card-worked' : ''}`}>
      {today && <div className="day-today-tag">TODAY</div>}
      <div>
        <div className="day-name">{dayName}</div>
        <div className="day-num">{date.getDate()}</div>
      </div>

      {hasShift ? (
        <div className="shift-block">
          <div className="shift-time">{fmt12(shift.clockIn)}</div>
          <div className="shift-arrow">to</div>
          <div className="shift-time">{fmt12(shift.clockOut)}</div>
          <div className="shift-divider" />
          <div className="shift-stats">
            <span className="shift-hours">{fmtHrs(shift.totalHours)}</span>
            <span className="shift-pay">{currency}{(shift.totalPay || 0).toFixed(2)}</span>
          </div>
        </div>
      ) : (
        <div className="day-off">
          <span className="day-off-icon">🌙</span>
          Day off
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;